import React, { useState } from 'react';
import { Modal, message } from 'antd';
import { APPOINTMENT_STATUS } from '../constants/constants';
import { formatDateET } from '../helpers/helpers';
import { useUpdateAppointmentStatusMutation } from '../services/appointments';

/**
 * Shared appointment status change behaviour: the confirmation flow for
 * destructive changes / reversals, the in-flight per-appointment loading state
 * and the status mutation itself.
 *
 * @param {Function} onSuccess  called as (newStatus, appointmentId) once the
 *                              status has been written, and on cancel with the
 *                              unchanged status so callers can re-sync the UI.
 * @param {Number}   zIndex     zIndex for the confirm dialog. Pass a value above
 *                              the host modal when calling from inside one.
 */
export const useAppointmentStatusChange = ({ onSuccess, zIndex } = {}) => {
  const [updatingStatusIds, setUpdatingStatusIds] = useState(new Set());
  const [updateAppointmentStatus] = useUpdateAppointmentStatusMutation();

  const updateStatus = async (status, id) => {
    setUpdatingStatusIds(prev => new Set([...prev, id]));
    try {
      await updateAppointmentStatus({
        id,
        body: { status: status },
      });
      onSuccess?.(status, id);
      if (status === APPOINTMENT_STATUS.CANCELLED) {
        message.success('Appointment cancelled successfully');
      }
    } finally {
      setUpdatingStatusIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleStatusChange = (newStatus, appointmentData) => {
    const currentStatus = appointmentData?.status;
    const customerName = appointmentData?.user?.username || 'Unknown Customer';
    const startDate = appointmentData?.appointment_start_time
      ? formatDateET(appointmentData.appointment_start_time, 'DD-MMM-YYYY')
      : 'N/A';
    const endDate = appointmentData?.appointment_end_time
      ? formatDateET(appointmentData.appointment_end_time, 'DD-MMM-YYYY')
      : 'N/A';
    const dateRange =
      startDate === endDate ? startDate : `${startDate} to ${endDate}`;

    // Determine if confirmation is needed
    const isDestructive =
      newStatus === APPOINTMENT_STATUS.CANCELLED ||
      newStatus === APPOINTMENT_STATUS.REJECTED;
    const isReversal =
      currentStatus === APPOINTMENT_STATUS.CHECKED_OUT ||
      currentStatus === APPOINTMENT_STATUS.CANCELLED;
    const needsConfirmation = isDestructive || isReversal;

    if (needsConfirmation) {
      let title = 'Confirm Status Change';
      let warningMessage =
        'Are you sure you want to change this appointment status?';

      if (newStatus === APPOINTMENT_STATUS.CANCELLED) {
        title = 'Confirm Appointment Cancellation';
        warningMessage = 'Are you sure you want to cancel this appointment?';
      } else if (newStatus === APPOINTMENT_STATUS.REJECTED) {
        title = 'Confirm Appointment Rejection';
        warningMessage = 'Are you sure you want to reject this appointment?';
      } else if (currentStatus === APPOINTMENT_STATUS.CHECKED_OUT) {
        title = 'Confirm Status Reversal';
        warningMessage =
          'This appointment was already checked out. Are you sure you want to change its status?';
      } else if (currentStatus === APPOINTMENT_STATUS.CANCELLED) {
        title = 'Confirm Reactivation';
        warningMessage =
          'This appointment was cancelled. Are you sure you want to reactivate it?';
      }

      Modal.confirm({
        title,
        zIndex,
        content: (
          <div>
            <p>{warningMessage}</p>
            <div
              style={{
                marginTop: '12px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <p>
                <strong>Customer:</strong> {customerName}
              </p>
              <p>
                <strong>Service:</strong>{' '}
                {appointmentData?.service?.title || 'N/A'}
              </p>
              <p>
                <strong>Date(s):</strong> {dateRange}
              </p>
              <p>
                <strong>Current Status:</strong> {currentStatus || 'pending'}
              </p>
              <p>
                <strong>New Status:</strong> {newStatus}
              </p>
            </div>
          </div>
        ),
        okText: 'Yes, Change Status',
        cancelText: 'No',
        okButtonProps: {
          style: {
            backgroundColor: isDestructive ? '#ef4444' : '#10b981',
            borderColor: isDestructive ? '#ef4444' : '#10b981',
            color: '#ffffff',
          },
        },
        cancelButtonProps: {
          style: {
            backgroundColor: '#ffffff',
            borderColor: '#d1d5db',
            color: '#374151',
          },
        },
        onOk: async () => {
          await updateStatus(newStatus, appointmentData?._id);
        },
        onCancel: () => {
          // Force re-render to revert dropdown state
          onSuccess?.(currentStatus, appointmentData?._id);
        },
      });
    } else {
      // For non-destructive changes, proceed directly
      updateStatus(newStatus, appointmentData?._id);
    }
  };

  return { handleStatusChange, updateStatus, updatingStatusIds };
};
